import React, { useMemo, useState, useRef, useEffect } from "react";
import Card from "@mui/material/Card";
import CardContent from "@mui/material/CardContent";
import Typography from "@mui/material/Typography";
import { Box, CircularProgress, Grid, Stack } from "@mui/material";
import {
  Order,
  canAddChangeOrder,
  canDeleteOrder,
} from "../../types/order";

import { formatAsDate } from "../../utils/date";
import { formatAsCurrency } from "../../utils/number";
import {
  MoreMenuButton,
  MoreMenuButtonProps,
} from "../../components/MoreMenuButton";
import { OrderPhaseChip } from "./OrderPhaseChip";
import { useSession } from "../../hooks/useSession";
import FlightIcon from '@mui/icons-material/Flight';

type OrderCardProps = {
  order: Order;
  isSaving?: boolean;
  handleOrderEditClick: (orderGuid: string) => void;
  handleNewChangeOrderClick: (orderGuid: string) => void;
  handleDeleteOrderClick: (orderGuid: string) => void;
  handleConvertToQuoteClick: (prospectGuid: string) => void;
  handleUnconfirmClick: (prospectGuid: string) => void;
};

export const OrderCard: React.FC<OrderCardProps> = ({
  order,
  isSaving = false,
  handleOrderEditClick,
  handleNewChangeOrderClick,
  handleDeleteOrderClick,
  handleConvertToQuoteClick,
  handleUnconfirmClick,
}) => {
  const { user } = useSession();

  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const currentRef = ref.current; // Store ref.current in a variable
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      });
    });

    if (currentRef) {
      observer.observe(currentRef);
    }

    return () => {
      if (currentRef) {
        observer.unobserve(currentRef); // Use the stored variable
      }
    };
  }, []);

  const menuItems = useMemo(() => {
    const items: MoreMenuButtonProps["menuItems"] = [
      {
        label: "Edit",
        onClick: () => handleOrderEditClick(order.orderGuid),
      },
    ];

    if (["admin","owner"].includes(user?.userRole ?? "") && !order.isUnconfirmed && !["q","a"].includes(order.orderStatus) && (!user?.isRetailUser || order?.isAutoPilot)) {
      items.push({
        label: "Unconfirm Order",
        onClick: () => handleUnconfirmClick(order.orderGuid),
      });
    }

    if (order.orderStatus === "p") {
      items.push({
        label: "Convert To Quote",
        onClick: () => handleConvertToQuoteClick(order.orderGuid),
      });
    }

    if (canAddChangeOrder(order) && (!user?.isRetailUser || order?.isAutoPilot)) {
      items.push({
        label: "New Sales Change Order",
        onClick: () => handleNewChangeOrderClick(order.orderGuid),
      });
    }

    if (user && canDeleteOrder(order, user)) {
      items.push({
        label: "Delete",
        onClick: () => handleDeleteOrderClick(order.orderGuid),
      });
    }

    return items;
  }, [
    handleDeleteOrderClick,
    handleNewChangeOrderClick,
    handleOrderEditClick,
    handleConvertToQuoteClick,
    order,
    user,
    handleUnconfirmClick,
  ]);

  return (
    <Card
      ref={ref}
      sx={{
        mb: 2,
        touchAction: "none",
        boxShadow: "0px 1px 2px 0px #1018280D",
        cursor: "grab",
        position: "relative",
      }}
    >
      {isSaving && (
        <Box
          sx={{
            position: "absolute",
            inset: 0,
            zIndex: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            backgroundColor: "rgba(255, 255, 255, 0.6)",
            borderRadius: "inherit",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      )}
      {isVisible && (
        <CardContent
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            height: user?.isRetailUser ? "210px" : "176px",
          }}
        >
          <Box>
            <Stack direction="row" spacing={1} justifyContent="space-between">
              <Typography
                variant="h5"
                component="div"
                sx={{
                  fontSize: 18,
                  cursor: "pointer",
                }}
                onClick={() => handleOrderEditClick(order.orderGuid)}
              >
                ID {order.orderIdentifier}
              </Typography>
              <Stack direction="row">
                {order.isAutoPilot && (
                  <FlightIcon color="primary" />
                )}
                {order.orderStatus === "q" ? (
                  <Box
                    sx={{ cursor: "pointer" }}
                    onClick={() => handleOrderEditClick(order.orderGuid)}
                  >
                    <OrderPhaseChip quotePhase={order.quotePhase} />
                  </Box>
                ) : null}
                <div style={{ touchAction: "none" }}>
                  {menuItems.length > 0 && (
                    <MoreMenuButton
                      menuItems={menuItems}
                      componentProps={{ sx: { marginTop: -1, marginRight: -1 } }}
                    />
                  )}
                </div>
              </Stack>
            </Stack>
          </Box>
          <Typography noWrap sx={{ fontSize: 12 }} color="text.secondary">
            {order.calculatedVinNumber && `VIN: ${order.calculatedVinNumber}`}
          </Typography>
          <Typography sx={{ mb: 1.5, fontSize: 12 }} color="text.secondary">
            Created: {formatAsDate(order.createdTimestamp)}
          </Typography>
          <Box>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 12 }}
              noWrap
            >
              {order?.orderStatus === "p"
                ? ""
                : user?.isRetailUser
                ? `Manufacturer: ${order?.mfgTenantName}`
                : order?.soldAsType !== "y"
                ? `Retailer: ${
                    order?.retailerFriendlyName ||
                    order?.retTenantName
                  }`
                : ""}
            </Typography>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ fontSize: 12 }}
            >
              {order?.buyerLastName ? `Buyer: ${order?.buyerLastName ?? ""}` :
              order?.soldAsType === "r" || order?.orderStatus === "p"
                ? `Buyer: ${order?.buyerLastName ?? ""}`
                : ""}
            </Typography>
          </Box>
          {order.modelNumber && (
            <Grid
              container
              justifyContent="space-between"
              marginTop="auto"
              alignItems="flex-end"
            >
              <Grid item>
                <>
                  <Typography variant="caption" color="text.secondary">
                    Model
                  </Typography>
                  <Typography
                    noWrap
                    variant="body1"
                    sx={{ fontSize: 12, maxWidth: "180px" }}
                  >
                    {order.modelNumber}
                  </Typography>
                </>
              </Grid>
              {!["a", "p"].includes(order.orderStatus) && (
                <Grid item>
                  <Box width="100px">
                    {user?.isRetailUser ? (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Buyer Price
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: 12 }}>
                          {formatAsCurrency(order.buyerPrice)}
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="caption" color="text.secondary">
                          Base + Options
                        </Typography>
                        <Typography variant="body1" sx={{ fontSize: 12 }}>
                          {formatAsCurrency(order.basePlusOptions ?? 0)}
                        </Typography>
                      </>
                    )}
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
          {user?.isRetailUser && (
            <Grid
              container
              justifyContent="space-between"
              marginTop="14px"
              alignItems="flex-end"
            >
              {!["p"].includes(order.orderStatus) && (
                <Grid item>
                  <Typography variant="caption" color="text.secondary">
                    Created By
                  </Typography>
                  <Typography
                    variant="body1"
                    noWrap
                    sx={{ fontSize: 12, maxWidth: "180px" }}
                  >
                    {order.createdByText}
                  </Typography>
                </Grid>
              )}
              {!["a", "p"].includes(order.orderStatus) && (
                <Grid item>
                  <Box width="100px">
                    <Typography variant="caption" color="text.secondary">
                      Mfg Invoice Total
                    </Typography>
                    <Typography variant="body1" sx={{ fontSize: 12 }}>
                      {formatAsCurrency(order.invoicePrice ?? 0)}
                    </Typography>
                  </Box>
                </Grid>
              )}
            </Grid>
          )}
        </CardContent>
      )}
    </Card>
  );
};
